import React, { useMemo, useState } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";
import { CENIK } from "../../data/ceny";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: 20,
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
};

const titleStyle = {
  fontSize: 30,
  fontWeight: 900,
  color: "#111827",
  marginBottom: 8,
};

const subStyle = {
  color: "#6b7280",
  fontSize: 15,
  marginBottom: 20,
};

const sectionStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 16,
  marginBottom: 16,
  background: "#fafafa",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 10,
  padding: "8px 10px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#fff",
};

const thStyle = {
  textAlign: "left",
  fontSize: 13,
  color: "#374151",
  background: "#f3f4f6",
  borderBottom: "1px solid #e5e7eb",
  padding: "10px 8px",
};

const tdStyle = {
  borderBottom: "1px solid #e5e7eb",
  padding: "10px 8px",
  verticalAlign: "middle",
  fontSize: 14,
  color: "#111827",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "8px 12px",
  fontWeight: 800,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1px solid #93c5fd",
};

const SECTION_OPTIONS = [
  { value: "ostatni", label: "Ostatní" },
  { value: "prevoz", label: "Převoz" },
  { value: "rakev", label: "Rakev" },
  { value: "obrad", label: "Obřad" },
  { value: "urna", label: "Urna" },
  { value: "urna_navazujici", label: "Urna navazující" },
  { value: "kvetiny", label: "Květiny" },
  { value: "media", label: "Média" },
];

const SUBGROUP_OPTIONS = {
  ostatni: [{ value: "", label: "Bez podboxu" }],
  prevoz: [
    { value: "", label: "Bez podboxu" },
    { value: "doprava", label: "Doprava" },
    { value: "chlazeni_mrazeni", label: "Chlazení a mražení" },
    { value: "manipulace", label: "Manipulace" },
  ],
  rakev: [
    { value: "", label: "Bez podboxu" },
    { value: "rakev_priprava", label: "Rakev a příprava" },
    { value: "uprava_zesnuleho", label: "Úprava zesnulého" },
  ],
  obrad: [
    { value: "", label: "Bez podboxu" },
    { value: "hudba_recnik", label: "Hudba a řečník" },
    { value: "misto_a_vybava", label: "Místo a vybavení" },
  ],
  urna: [
    { value: "", label: "Bez podboxu" },
    { value: "urna_typ", label: "Typ urny" },
    { value: "urna_ukony", label: "Úkony s urnou" },
  ],
  urna_navazujici: [
    { value: "", label: "Bez podboxu" },
    { value: "urna_typ", label: "Typ urny" },
    { value: "urna_ukony", label: "Úkony s urnou" },
  ],
  kvetiny: [
    { value: "", label: "Bez podboxu" },
    { value: "zakladni_kvetiny", label: "Květiny a vazba" },
    { value: "vystaveni_kvetin", label: "Vystavení a aranžmá" },
  ],
  media: [
    { value: "", label: "Bez podboxu" },
    { value: "foto_video", label: "Foto / video / záznam" },
    { value: "projekce", label: "Projekce a přenos" },
  ],
};

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n) {
  return `${safeNumber(n).toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Kč`;
}

function getSubgroupOptions(sectionKey) {
  return SUBGROUP_OPTIONS[sectionKey || "ostatni"] || SUBGROUP_OPTIONS.ostatni;
}

function getSectionLabel(value) {
  return (
    SECTION_OPTIONS.find((opt) => String(opt.value) === String(value || "ostatni"))?.label ||
    "Ostatní"
  );
}

function getSubgroupLabel(sectionKey, subgroupKey) {
  const list = SUBGROUP_OPTIONS[sectionKey || "ostatni"] || SUBGROUP_OPTIONS.ostatni;
  return list.find((opt) => String(opt.value || "") === String(subgroupKey || ""))?.label || "Bez podboxu";
}

export default function Pohrebka2CenikForm() {
  const { order, setOrder } = usePohrebka2();
  const [saveMsg, setSaveMsg] = useState("");
  const [searchText, setSearchText] = useState("");
  const [newItem, setNewItem] = useState({
    nazev: "",
    cenaBezDph: "",
    dph: "12",
    jednotka: "ks",
    sekceKey: "ostatni",
    subgroupKey: "",
  });

  const STORAGE_KEY = "pohrebka2_global_cenik_overrides";

  const cenikList = Array.isArray(CENIK) ? CENIK : Object.values(CENIK || {});
  const overrides = order?.cenikOverrides || {};

  const rows = useMemo(() => {
    const customRows = Object.entries(overrides || {})
      .filter(([id, value]) => {
        if (!value || typeof value !== "object" || value.__custom !== true) return false;
        return !cenikList.some((item) => String(item?.id || "") === String(id || ""));
      })
      .map(([id, value]) => ({
        id,
        nazev: value?.nazev || "",
        cenaBezDph: value?.cenaBezDph ?? 0,
        dph: value?.dph ?? 12,
        jednotka: value?.jednotka ?? "ks",
        defaultQty: value?.defaultQty ?? 1,
        sekceKey: value?.sekceKey ?? "ostatni",
        subgroupKey: value?.subgroupKey ?? "",
        __custom: true,
      }));

    return [...cenikList, ...customRows].map((item) => {
      const override = overrides[item.id] || {};
      return {
        ...item,
        nazevFinal: override.nazev ?? item.nazev ?? "",
        cenaBezDphFinal: override.cenaBezDph ?? item.cenaBezDph ?? 0,
        dphFinal: override.dph ?? item.dph ?? 12,
        jednotkaFinal: override.jednotka ?? item.jednotka ?? "ks",
        defaultQtyFinal: override.defaultQty ?? item.defaultQty ?? 1,
        sekceKeyFinal: override.sekceKey ?? item.sekceKey ?? "ostatni",
        subgroupKeyFinal: override.subgroupKey ?? item.subgroupKey ?? "",
        isCustom: override.__custom === true || item.__custom === true,
      };
    });
  }, [cenikList, overrides]);

  const filteredRows = useMemo(() => {
    const needle = String(searchText || "").trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) => {
      const sekceLabel =
        SECTION_OPTIONS.find((opt) => String(opt.value) === String(row.sekceKeyFinal || "ostatni"))?.label || "";

      const subgroupLabel =
        getSubgroupOptions(row.sekceKeyFinal || "ostatni").find(
          (opt) => String(opt.value || "") === String(row.subgroupKeyFinal || "")
        )?.label || "";

      const hay = [
        row.id,
        row.nazevFinal,
        row.jednotkaFinal,
        row.sekceKeyFinal,
        row.subgroupKeyFinal,
        sekceLabel,
        subgroupLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(needle);
    });
  }, [rows, searchText]);

  const setOverride = (id, field, value) => {
    setOrder((prev) => ({
      ...(prev || {}),
      cenikOverrides: {
        ...(prev?.cenikOverrides || {}),
        [id]: {
          ...(prev?.cenikOverrides?.[id] || {}),
          [field]: value,
        },
      },
    }));
  };

  const setSectionOverride = (id, value) => {
    setOrder((prev) => ({
      ...(prev || {}),
      cenikOverrides: {
        ...(prev?.cenikOverrides || {}),
        [id]: {
          ...(prev?.cenikOverrides?.[id] || {}),
          sekceKey: value || "ostatni",
          subgroupKey: "",
        },
      },
    }));
  };

  const resetOverride = (id) => {
    setOrder((prev) => {
      const next = { ...(prev?.cenikOverrides || {}) };
      delete next[id];
      return {
        ...(prev || {}),
        cenikOverrides: next,
      };
    });
  };

  const addCustomItem = () => {
    const nazev = String(newItem?.nazev || "").trim();
    const cenaBezDph = Number(newItem?.cenaBezDph);
    const dph = Number(newItem?.dph);
    const jednotka = String(newItem?.jednotka || "").trim() || "ks";
    const sekceKey = String(newItem?.sekceKey || "").trim() || "ostatni";
    const subgroupKey = String(newItem?.subgroupKey || "").trim();

    if (!nazev) {
      setSaveMsg("Vyplň název nové položky.");
      setTimeout(() => setSaveMsg(""), 2500);
      return;
    }

    if (!Number.isFinite(cenaBezDph) || cenaBezDph <= 0) {
      setSaveMsg("Vyplň platnou cenu bez DPH.");
      setTimeout(() => setSaveMsg(""), 2500);
      return;
    }

    const slug = nazev
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "polozka";

    let nextId = `custom_${slug}`;
    let counter = 2;
    while (cenikList.some((item) => String(item?.id || "") === nextId) || overrides[nextId]) {
      nextId = `custom_${slug}_${counter}`;
      counter += 1;
    }

    setOrder((prev) => ({
      ...(prev || {}),
      cenikOverrides: {
        ...(prev?.cenikOverrides || {}),
        [nextId]: {
          id: nextId,
          __custom: true,
          nazev,
          cenaBezDph: String(cenaBezDph),
          dph: Number.isFinite(dph) ? String(dph) : "12",
          jednotka,
          sekceKey,
          subgroupKey,
          defaultQty: 1,
        },
      },
    }));

    setNewItem({
      nazev: "",
      cenaBezDph: "",
      dph: "12",
      jednotka: "ks",
      sekceKey: "ostatni",
      subgroupKey: "",
    });
    setSaveMsg(`Nová položka byla přidána: ${nextId}`);
    setTimeout(() => setSaveMsg(""), 2500);
  };

  const saveCenik = () => {
    try {
      const payload = order?.cenikOverrides || {};
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      setSaveMsg("Ceník byl uložen pro další nové zakázky na tomto zařízení.");
      setTimeout(() => setSaveMsg(""), 2500);
    } catch {
      setSaveMsg("Uložení ceníku se nepodařilo.");
      setTimeout(() => setSaveMsg(""), 2500);
    }
  };

  const loadSavedCenik = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setSaveMsg("Žádný uložený ceník nebyl nalezen.");
        setTimeout(() => setSaveMsg(""), 2500);
        return;
      }
      const parsed = JSON.parse(raw);
      setOrder((prev) => ({
        ...(prev || {}),
        cenikOverrides: parsed && typeof parsed === "object" ? parsed : {},
      }));
      setSaveMsg("Uložený ceník byl načten.");
      setTimeout(() => setSaveMsg(""), 2500);
    } catch {
      setSaveMsg("Načtení uloženého ceníku se nepodařilo.");
      setTimeout(() => setSaveMsg(""), 2500);
    }
  };

  const clearSavedCenik = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      setSaveMsg("Uložený ceník byl smazán.");
      setTimeout(() => setSaveMsg(""), 2500);
    } catch {
      setSaveMsg("Smazání uloženého ceníku se nepodařilo.");
      setTimeout(() => setSaveMsg(""), 2500);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>Editor ceníku</div>
      <div style={subStyle}>
        Změny se ukládají do této zakázky a okamžitě se propíšou do Výběru služeb a Finální objednávky.
      </div>

      <div style={sectionStyle}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
          <span style={badgeStyle}>Položek: {rows.length}</span>
          <span style={badgeStyle}>Po filtru: {filteredRows.length}</span>
          <span style={badgeStyle}>Upraveno: {Object.keys(overrides).length}</span>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Hledat v ceníku: název, ID, box, podbox..."
            style={{ ...inputStyle, maxWidth: 360, background: "#fff" }}
          />
        </div>

        <div style={{ ...sectionStyle, background: "#ffffff", marginBottom: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 10, color: "#111827" }}>Nová položka do ceníku</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(200px,2fr) 140px 90px 100px 170px 170px 150px",
              gap: 10,
              alignItems: "end",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Název</div>
              <input
                value={newItem.nazev}
                onChange={(e) => setNewItem((prev) => ({ ...prev, nazev: e.target.value }))}
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Cena bez DPH</div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newItem.cenaBezDph}
                onChange={(e) => setNewItem((prev) => ({ ...prev, cenaBezDph: e.target.value }))}
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>DPH %</div>
              <input
                type="number"
                min="0"
                step="1"
                value={newItem.dph}
                onChange={(e) => setNewItem((prev) => ({ ...prev, dph: e.target.value }))}
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Jednotka</div>
              <input
                value={newItem.jednotka}
                onChange={(e) => setNewItem((prev) => ({ ...prev, jednotka: e.target.value }))}
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Box / sekce</div>
              <select
                value={newItem.sekceKey}
                onChange={(e) => setNewItem((prev) => ({ ...prev, sekceKey: e.target.value, subgroupKey: "" }))}
                style={{ ...inputStyle, width: "100%" }}
              >
                {SECTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Podbox / skupina</div>
              <select
                value={newItem.subgroupKey || ""}
                onChange={(e) => setNewItem((prev) => ({ ...prev, subgroupKey: e.target.value }))}
                style={{ ...inputStyle, width: "100%" }}
              >
                {getSubgroupOptions(newItem.sekceKey || "ostatni").map((opt) => (
                  <option key={opt.value || "empty"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={addCustomItem}
              style={{
                border: "1px solid #16a34a",
                background: "#16a34a",
                color: "#fff",
                borderRadius: 10,
                padding: "10px 14px",
                cursor: "pointer",
                fontWeight: 800,
              }}
            >
              Přidat položku
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <button
            type="button"
            onClick={saveCenik}
            style={{
              border: "1px solid #1d4ed8",
              background: "#1d4ed8",
              color: "#fff",
              borderRadius: 10,
              padding: "10px 14px",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            Uložit ceník
          </button>

          <button
            type="button"
            onClick={loadSavedCenik}
            style={{
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#111827",
              borderRadius: 10,
              padding: "10px 14px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Načíst uložený
          </button>

          <button
            type="button"
            onClick={clearSavedCenik}
            style={{
              border: "1px solid #fecaca",
              background: "#fff1f2",
              color: "#991b1b",
              borderRadius: 10,
              padding: "10px 14px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Smazat uložený
          </button>
        </div>

        {saveMsg ? (
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 10,
              background: "#eff6ff",
              color: "#1d4ed8",
              border: "1px solid #bfdbfe",
              fontWeight: 700,
            }}
          >
            {saveMsg}
          </div>
        ) : null}

        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Název</th>
                <th style={thStyle}>Cena bez DPH</th>
                <th style={thStyle}>DPH %</th>
                <th style={thStyle}>Jednotka</th>
                <th style={thStyle}>Box</th>
                <th style={thStyle}>Podbox</th>
                <th style={thStyle}>Reset</th>
              </tr>
            </thead>
            <tbody>
              {!filteredRows.length ? (
                <tr>
                  <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: "#6b7280", padding: 18 }}>
                    Nic neodpovídá hledání.
                  </td>
                </tr>
              ) : null}

              {filteredRows.map((row) => {
                const isChanged = !!overrides[row.id];

                return (
                  <tr key={row.id} style={isChanged ? { background: "#eff6ff" } : undefined}>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap", fontSize: 12 }}>{row.id}</td>

                    <td style={tdStyle}>
                      <input
                        value={row.nazevFinal}
                        onChange={(e) => setOverride(row.id, "nazev", e.target.value)}
                        style={inputStyle}
                      />
                    </td>

                    <td style={{ ...tdStyle, width: 170 }}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.cenaBezDphFinal}
                        onChange={(e) => setOverride(row.id, "cenaBezDph", e.target.value)}
                        style={inputStyle}
                      />
                      <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
                        {formatMoney(row.cenaBezDphFinal)}
                      </div>
                    </td>

                    <td style={{ ...tdStyle, width: 100 }}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={row.dphFinal}
                        onChange={(e) => setOverride(row.id, "dph", e.target.value)}
                        style={inputStyle}
                      />
                    </td>

                    <td style={{ ...tdStyle, width: 120 }}>
                      <input
                        value={row.jednotkaFinal}
                        onChange={(e) => setOverride(row.id, "jednotka", e.target.value)}
                        style={inputStyle}
                      />
                    </td>

                    <td style={{ ...tdStyle, width: 160 }}>
                      <select
                        value={row.sekceKeyFinal || "ostatni"}
                        onChange={(e) => setSectionOverride(row.id, e.target.value)}
                        style={inputStyle}
                      >
                        {SECTION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td style={{ ...tdStyle, width: 180 }}>
                      <select
                        value={row.subgroupKeyFinal || ""}
                        onChange={(e) => setOverride(row.id, "subgroupKey", e.target.value)}
                        style={inputStyle}
                      >
                        {getSubgroupOptions(row.sekceKeyFinal || "ostatni").map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td style={{ ...tdStyle, width: 120 }}>
                      <button
                        type="button"
                        onClick={() => resetOverride(row.id)}
                        style={{
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          borderRadius: 10,
                          padding: "8px 10px",
                          cursor: "pointer",
                          fontWeight: 700,
                        }}
                      >
                        {row.isCustom ? "Smazat" : "Reset"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
